import { useEntityId } from '@/lib/offline/operations';
// Editar camada (M5, v0.5 — D5 revisada: edición parcial).
//
// Ruta: /camadas/:camadaId/editar
//
// Campos editables (solo si la camada NO está cerrada):
//   fecha_entrada, incubadora_origen, edad_dias_entrada,
//   fecha_prevista_salida. El peso de entrada se calcula desde
//   las llegadas de animales y se muestra como solo lectura.
// Inmutables (se muestran deshabilitados): codigo_lote, fase,
// sexo. El backend los ignora si llegan (forward-compatible).
//
// Si la camada está cerrada → aviso + volver al detalle (el
// backend rechazaría el PATCH con 409).

import { useEffect, useRef, useState } from 'react';
import { Link, useBeforeUnload, useBlocker, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { useCamada, useEditarCamada } from '@/lib/queries/camadas';
import { useToast } from '@/components/toastStore';
import { extractApiMessage } from '@/lib/api';
import { ModalOverlay } from '@/components/ModalOverlay';

interface FormState {
  fecha_entrada: string;
  edad_dias_entrada: string;
  fecha_prevista_salida: string;
  incubadora_origen: string;
}

export function CamadaEditarPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { camadaId: camadaIdParam } = useParams<{ camadaId: string }>();
  const camadaId = useEntityId(camadaIdParam, 'camadas');

  const q = useCamada(camadaId);
  const editar = useEditarCamada();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const dirty =
    !!form &&
    !!q.data &&
    (form.fecha_entrada !== (q.data.fecha_entrada ?? '') ||
      form.edad_dias_entrada !== String(q.data.edad_dias_entrada ?? '') ||
      form.fecha_prevista_salida !== (q.data.fecha_prevista_salida ?? '') ||
      form.incubadora_origen !== (q.data.incubadora_origen ?? ''));
  const dirtyRef = useRef(dirty);
  const saving = useRef(false);
  dirtyRef.current = dirty;
  const blocker = useBlocker(() => dirtyRef.current || saving.current);
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  useBeforeUnload((event) => {
    if (dirtyRef.current || saving.current) {
      event.preventDefault();
      event.returnValue = '';
    }
  });
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  // Hidratar el form cuando la query resuelve. useEffect (no
  // values de useForm) porque no usamos react-hook-form aquí: son
  // pocos campos y queremos inputs de texto simples.
  useEffect(() => {
    if (q.data && form == null) {
      setForm({
        fecha_entrada: q.data.fecha_entrada ?? '',
        edad_dias_entrada: q.data.edad_dias_entrada != null ? String(q.data.edad_dias_entrada) : '',
        fecha_prevista_salida: q.data.fecha_prevista_salida ?? '',
        incubadora_origen: q.data.incubadora_origen ?? '',
      });
    }
  }, [q.data, form]);

  const departureDialog = blocker.state === 'blocked' && (
    <ModalOverlay
      ariaLabel="Cambios sin guardar"
      placement="bottom"
      className="max-w-md"
      onBackdrop={() => blocker.reset()}
    >
      <h2 className="ui-page-title">{busy ? 'Guardando…' : 'Cambios sin guardar'}</h2>
      <p className="py-3">
        {busy
          ? 'Espera a que termine el guardado antes de salir.'
          : 'Puedes seguir editando o salir sin guardar este borrador.'}
      </p>
      <button className="ui-primary w-full" onClick={() => blocker.reset()}>
        Seguir editando
      </button>
      {!busy && (
        <button
          className="ui-secondary mt-2 w-full"
          onClick={() => {
            dirtyRef.current = false;
            blocker.proceed();
          }}
        >
          Salir sin guardar
        </button>
      )}
    </ModalOverlay>
  );

  if (q.isLoading) {
    return (
      <main className="container mx-auto max-w-md px-4 py-6">
        <p className="text-text-secondary">{t('common.loading')}</p>
        {departureDialog}
      </main>
    );
  }

  if (q.isError || !q.data) {
    return (
      <main className="container mx-auto max-w-md px-4 py-6">
        <p
          role="alert"
          className="rounded-md border border-warning-line bg-warning-bg px-3 py-2 text-sm text-warning-text"
        >
          {String((q.error as Error)?.message ?? t('common.error'))}
        </p>
        {departureDialog}
      </main>
    );
  }

  const camada = q.data;

  if (camada.cerrada) {
    return (
      <main className="container mx-auto max-w-md px-4 py-6">
        <p className="rounded-md border border-warning-line bg-warning-bg px-3 py-2 text-sm text-warning-text">
          {t('camada_editar.help_solo_si_no_cerrada')}
        </p>
        <Link
          to={`/camadas/${camada.id}`}
          className="mt-3 inline-block text-sm text-accent hover:underline"
        >
          ← {t('camada_detalle.volver')}
        </Link>
        {departureDialog}
      </main>
    );
  }

  function set<K extends keyof FormState>(key: K, value: string) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function onGuardar() {
    if (!form || camadaId == null || saving.current) return;
    const body: Record<string, unknown> = {};
    if (form.fecha_entrada && form.fecha_entrada !== camada.fecha_entrada)
      body.fecha_entrada = form.fecha_entrada;
    if (
      form.edad_dias_entrada !== '' &&
      form.edad_dias_entrada !== String(camada.edad_dias_entrada ?? '')
    )
      body.edad_dias_entrada = Number(form.edad_dias_entrada);
    if (form.fecha_prevista_salida !== (camada.fecha_prevista_salida ?? ''))
      body.fecha_prevista_salida = form.fecha_prevista_salida || null;
    if (form.incubadora_origen !== (camada.incubadora_origen ?? ''))
      body.incubadora_origen = form.incubadora_origen || null;

    if (Object.keys(body).length === 0) {
      dirtyRef.current = false;
      navigate(`/camadas/${camadaId}`);
      return;
    }

    saving.current = true;
    setBusy(true);
    setError(null);
    try {
      await editar.mutateAsync({ id: camadaId, data: body });
      await queryClient.invalidateQueries({ queryKey: ['camadas'] });
      toast.show({ kind: 'success', message: t('camada_editar.exito') });
      dirtyRef.current = false;
      saving.current = false;
      if (blockerRef.current.state === 'blocked') blockerRef.current.reset();
      navigate(`/camadas/${camadaId}`);
    } catch (err) {
      setError(`${t('camada_detalle.errores_editar')}: ${extractApiMessage(err)}`);
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }

  return (
    <main className="ui-page pb-8">
      <h1 className="ui-page-title">{t('camada_editar.titulo', { codigo: camada.codigo_lote })}</h1>
      <p className="mb-4 mt-1 text-sm text-text-secondary">{t('camada_editar.subtitulo')}</p>
      {error && (
        <div className="parte-error mb-4" role="alert" ref={errorRef} tabIndex={-1}>
          {error}
        </div>
      )}

      {form && (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void onGuardar();
          }}
        >
          {/* Bloque: campos inmutables (informativos) */}
          <fieldset className="rounded-lg border border-border bg-surface p-3">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-text-secondary">
              {t('camada_editar.seccion_camada')}
            </legend>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  className="block text-sm font-medium text-text-secondary"
                  htmlFor="codigo_lote_ro"
                >
                  {t('camada.codigo_lote')}
                </label>
                <input
                  id="codigo_lote_ro"
                  type="text"
                  value={camada.codigo_lote}
                  disabled
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-text-muted"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary" htmlFor="sexo_ro">
                  {t('camadas.filtro_sexo')}
                </label>
                <input
                  id="sexo_ro"
                  type="text"
                  value={
                    camada.sexo === 'machos'
                      ? t('camadas.filtro_sexo_machos')
                      : t('camadas.filtro_sexo_hembras')
                  }
                  disabled
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-text-muted"
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-text-muted">
              {t('camada_editar.help_codigo_inmutable')}
            </p>
          </fieldset>

          {/* Bloque: campos editables */}
          <fieldset disabled={busy} className="rounded-lg border border-border bg-surface p-3">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-text-secondary">
              {t('camada_editar.seccion_fechas')}
            </legend>
            <div className="space-y-3">
              <div>
                <label
                  className="block text-sm font-medium text-text-primary"
                  htmlFor="fecha_entrada"
                >
                  {t('camada_editar.label_fecha_entrada')}
                </label>
                <input
                  id="fecha_entrada"
                  type="date"
                  value={form.fecha_entrada}
                  onChange={(e) => set('fecha_entrada', e.target.value)}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium text-text-primary"
                  htmlFor="edad_dias_entrada"
                >
                  {t('camada_editar.label_edad_dias_entrada')}
                </label>
                <input
                  id="edad_dias_entrada"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={form.edad_dias_entrada}
                  onChange={(e) => set('edad_dias_entrada', e.target.value)}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium text-text-primary"
                  htmlFor="peso_medio_kg_entrada"
                >
                  {t('camada_editar.label_peso_medio_kg_entrada')}
                </label>
                <input
                  id="peso_medio_kg_entrada"
                  type="text"
                  value={
                    camada.peso_medio_kg_entrada != null ? String(camada.peso_medio_kg_entrada) : ''
                  }
                  readOnly
                  aria-describedby="peso-entrada-help"
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-text-secondary"
                />
                <p id="peso-entrada-help" className="mt-2 text-xs text-text-secondary">
                  Calculado como peso total dividido entre animales recibidos. Para corregirlo,
                  revisa las llegadas de camiones.
                </p>
              </div>
              <div>
                <label
                  className="block text-sm font-medium text-text-primary"
                  htmlFor="fecha_prevista_salida"
                >
                  {t('camada_editar.label_fecha_prevista_salida')}
                </label>
                <input
                  id="fecha_prevista_salida"
                  type="date"
                  value={form.fecha_prevista_salida}
                  onChange={(e) => set('fecha_prevista_salida', e.target.value)}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none"
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium text-text-primary"
                  htmlFor="incubadora_origen"
                >
                  {t('camada_editar.label_incubadora_origen')}
                </label>
                <input
                  id="incubadora_origen"
                  type="text"
                  value={form.incubadora_origen}
                  onChange={(e) => set('incubadora_origen', e.target.value)}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none"
                />
              </div>
            </div>
          </fieldset>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigate(`/camadas/${camadaId}`)}
              className="ui-secondary flex-1"
            >
              {t('camada_editar.cancelar')}
            </button>
            <button
              type="submit"
              disabled={busy}
              className="ui-primary flex-1"
              data-testid="camada-editar-guardar"
            >
              {busy ? 'Guardando…' : t('camada_editar.guardar')}
            </button>
          </div>
        </form>
      )}
      {departureDialog}
    </main>
  );
}

export default CamadaEditarPage;
